import { Col, Radio, Row, InputNumber, Select } from 'antd';
import { DatePickerField, InputField, SelectField } from 'components/FormFields';
import { Controller, useForm, FormProvider } from 'react-hook-form';
import CertificationsField from './CertificationsField';
import SkillsField from './SkillsField';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'app/hooks';
import { selectShowMoney } from 'features/auth/authSlice';
import { useMaskValue } from 'hooks';
import numeral from 'numeral';
import { selectCurrencyFormat } from 'app/commonRedux/appSlice';

// Component hiển thị giá trị tiền tệ mà không có đơn vị
const MaskedValueRendererWithoutUnit: React.FC<{ value: number | string }> = ({ value }) => {
  const isShowMoney = useAppSelector(selectShowMoney);
  const currencyFormat = useAppSelector(selectCurrencyFormat);
  const maskedValue = useMaskValue(isNaN(Number(value)) || !value ? 0 : value, isShowMoney);
  return maskedValue !== '*****' ? numeral(maskedValue).format(currencyFormat) : maskedValue;
};

interface IItemInput {
  label: string;
  name: string;
  required?: boolean;
  options?: Array<any>;
  fieldType: 'select' | 'input' | 'datePicker' | 'radio' | 'certifications' | 'skills' | 'salary';
}

interface ProfessionalAcademicProps {
  handleChange: any;
  data: any;
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProfessionalAcademic = ({
  handleChange,
  data,
  visible,
  setVisible,
}: ProfessionalAcademicProps) => {
  const { t } = useTranslation(['recruitment', 'action']);
  const isShowMoney = useAppSelector(selectShowMoney);
  const methods = useForm({
    resolver: undefined,
    defaultValues: data || {},
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (data) reset(data || {});
  }, [data, reset]);

  const onSubmit = (data: any) => {
    handleChange && handleChange(data);
  };

  const itemsInputLeft: Array<IItemInput> = [
    {
      label: t('recruitment:current_job_title'),
      name: 'currentJob',
      fieldType: 'input',
      required: true,
    },
    {
      label: t('recruitment:current_company'),
      name: 'currentCompany',
      fieldType: 'input',
      // required: true,
    },
    {
      label: t('recruitment:university'),
      name: 'university',
      fieldType: 'input',
    },
  ];

  const itemsInputRight: Array<IItemInput> = [
    {
      label: t('recruitment:current_salary'),
      name: 'currentSalary',
      fieldType: 'salary',
    },
    {
      label: t('recruitment:expected_salary'),
      name: 'expectSalary',
      fieldType: 'salary',
      // required: true,
    },
    {
      label: t('recruitment:diploma'),
      name: 'diploma',
      fieldType: 'input',
    },
  ];

  const renderItemEdit = (
    fieldType: 'select' | 'input' | 'datePicker' | 'radio' | 'certifications' | 'skills' | 'salary',
    item: IItemInput,
  ) => {
    switch (fieldType) {
      case 'select':
        return (
          <SelectField
            disabled={!visible}
            control={control}
            options={item?.options || []}
            name={item.name}
            onChange={handleSubmit(onSubmit)}
          />
        );
      case 'input':
        return (
          <InputField
            disabled={!visible}
            control={control}
            name={item.name}
            onChange={handleSubmit(onSubmit)}
          />
        );
      case 'datePicker':
        return (
          <DatePickerField
            disabled={!visible}
            style={{ width: '100%' }}
            control={control}
            name={item.name}
            onChange={handleSubmit(onSubmit)}
          />
        );
      case 'radio':
        return (
          <Controller
            name={item.name}
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                disabled={!visible}
                options={item?.options || []}
                onChange={handleSubmit(onSubmit)}
              />
            )}
          />
        );
      case 'certifications':
        return (
          <CertificationsField
            disabled={!visible}
            name={item.name}
            onChange={handleSubmit(onSubmit)}
          />
        );
      case 'skills':
        return (
          <SkillsField disabled={!visible} name={item.name} onChange={handleSubmit(onSubmit)} />
        );
      case 'salary':
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Controller
              name={item.name}
              control={control}
              render={({ field }) =>
                visible ? (
                  <InputNumber
                    {...field}
                    disabled={!visible}
                    style={{ width: '100%' }}
                    min={0}
                    placeholder="******"
                    formatter={(value) => {
                      if (!isShowMoney) return '******';
                      return value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
                    }}
                    parser={(value: any) => {
                      if (value === '******') return field.value;
                      return value ? value.replace(/\$\s?|(,*)/g, '') : '';
                    }}
                    onChange={(value) => {
                      field.onChange(value);
                      handleSubmit(onSubmit)();
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      background: '#f5f5f5',
                      padding: '4px 11px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '2px',
                      minHeight: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <MaskedValueRendererWithoutUnit value={field.value || 0} />
                  </div>
                )
              }
            />
            <Controller
              name={`currencySymbol`}
              control={control}
              defaultValue="VND"
              render={({ field }) => (
                <Select
                  {...field}
                  disabled={!visible}
                  style={{ width: 100 }}
                  options={[
                    { label: 'USD', value: 'USD' },
                    { label: 'VND', value: 'VND' },
                  ]}
                  onChange={(value) => {
                    field.onChange(value);
                    handleSubmit(onSubmit)();
                  }}
                />
              )}
            />
          </div>
        );
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row gutter={64}>
          <Col span={12}>
            {itemsInputLeft.map((item) => (
              <Row key={item.name} style={{ marginBottom: 8 }}>
                <Col span={8}>
                  <label>
                    {item.label}
                    {item.required && <span style={{ color: 'red' }}> *</span>}
                  </label>
                </Col>
                <Col span={16}>{renderItemEdit(item.fieldType, item)}</Col>
              </Row>
            ))}
          </Col>
          <Col span={12}>
            {itemsInputRight.map((item) => (
              <Row key={item.name} style={{ marginBottom: 8 }}>
                <Col span={8}>
                  <label>
                    {item.label}
                    {item.required && <span style={{ color: 'red' }}> *</span>}
                  </label>
                </Col>
                <Col span={16}>{renderItemEdit(item.fieldType, item)}</Col>
              </Row>
            ))}
          </Col>
        </Row>
      </form>
    </FormProvider>
  );
};

export default ProfessionalAcademic;
